import { BlockEditorInput } from "@babackapp/blocks-editor/block-editor-input";
import { useVariablesDictionary } from "@babackapp/editor-blocks-library/variables-context";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import { Alert, AlertDescription, AlertTitle } from "@babackapp/ui/components/alert";
import { FloatingPanelContent, FloatingPanelSeparator } from "@babackapp/ui/components/floating-panel";
import { FormField } from "@babackapp/ui/components/form";
import { useTranslation } from "react-i18next";
import { Info, SquareArrowOutUpRight } from "lucide-react";
import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useEditorHistoryContext } from "@babackapp/blocks-editor/contexts/editor-history-context";
import { useEmailEditorLocale } from "../../context/templates-editor-locale-context";
import { useTemplatesEditorConfig } from "../../context/templates-editor-config-context";
import { useTemplatesEditorTemplates } from "../../context/templates-editor-templates-context";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import { TranslatableField } from "@babackapp/ui/components/translatable-field";

import useTemplateWorkflowsDependencies from "../../hooks/use-template-workflows-dependencies";
import { EmailTemplateFormData } from "../../types";


export default function LeftSidebarEditionPanel() {
  const { t } = useTranslation("trackoffice");
  const { control, watch, setValue } = useFormContext<EmailTemplateFormData>();
  const { showWorkflowDependencies } = useTemplatesEditorConfig();
  const templateType = watch("type") || "email";

  const subject = watch("subject");
  const { variables } = useVariablesDictionary();
  const { availableLocales, currentLocale } = useEmailEditorLocale();
  const { captureState } = useEditorHistoryContext();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return (
    <FloatingPanelContent>
      {showWorkflowDependencies !== false && <WorkflowDependencyAlert />}
      <div className="flex gap-2 flex-col">
        <ContextualTipsTrigger
          wrapped
          tips={[
            {
              id: "template-title",
              title: t(
                "templatesEditor.tips.templateTitle.title",
                "Template's title",
              ),
              description: t(
                "templatesEditor.tips.templateTitle.description",
                "Name your template so you can easily identify it in the list of templates. This will never be shown to your users.",
              ),
            },
          ]}
        >
          <FormField
            control={control}
            name={`title`}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                  {t(
                    "notificationsTemplatesEditor.emailEditor.header.templateNameTitle",
                    `Template name`,
                  )}
                </span>
                <FormInput
                  label=""
                  value={field.value as string}
                  onChange={(value) => {
                    field.onChange(value);
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }
                    debounceTimerRef.current = setTimeout(() => {
                      captureState();
                    }, 300);
                  }}
                  placeholder={t(
                    "notificationsTemplatesEditor.emailEditor.header.templateNameTitlePlaceholder",
                    `Give a name to this template...`,
                  )}
                  className="text-sm"
                />
              </div>
            )}
          />
        </ContextualTipsTrigger>
        <FloatingPanelSeparator />
        <ContextualTipsTrigger
          wrapped
          tips={[
            {
              id: "template-subject",
              title: t(
                "templatesEditor.tips.templateSubject.title",
                "Template's subject",
              ),
              description: t(
                "templatesEditor.tips.templateSubject.description",
                "You can customize the subject of the email using variables. Don't forget to translate the subject in the language of your users!",
              ),
            },
          ]}
        >
          <div className="flex flex-col gap-1">
            <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
              {templateType === "email"
                ? t(
                  "notificationsTemplatesEditor.emailEditor.header.subjectTitle",
                  `Default subject`,
                )
                : t(
                  "notificationsTemplatesEditor.emailEditor.header.titleTitle",
                  "Default title",
                )
              }
            </span>
            <TranslatableField
              value={subject as Record<string, string>}
              onChange={(value) => {
                setValue("subject", value, { shouldDirty: true });
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                }
                debounceTimerRef.current = setTimeout(() => {
                  captureState();
                }, 300);
              }}
              locales={availableLocales.map((l) => l.value)}
              defaultLocale={currentLocale}
              placeholder={t(
                "notificationsTemplatesEditor.emailEditor.header.subjectPlaceholder",
                `Enter email subject...`,
              )}
              className="text-sm"
              renderInput={({ value, onChange, placeholder }) => (
                <BlockEditorInput
                  variables={variables}
                  value={value}
                  onChange={onChange}
                  placeholder={placeholder}
                  outputFormat="text"
                  className="pr-16"
                />
              )}
            />
          </div>
        </ContextualTipsTrigger>
        {/**
         * Note before enabling the preview field:
         * - The templates preview is currently not used when sending emails (default templates preview is used instead).
         * - Before using templates preview, we MUST ensure that the existing templates have a proper preview set (not the case for most shop because of broken variables)
         **/}
        <div className="hidden">
          <FormField
            control={control}
            name="preview"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                  {t(
                    "notificationsTemplatesEditor.emailEditor.header.previewTitle",
                    `Default preview`,
                  )}
                </span>
                <TranslatableField
                  value={field.value as Record<string, string>}
                  onChange={field.onChange}
                  locales={availableLocales.map((l) => l.value)}
                  defaultLocale={currentLocale}
                  placeholder={t(
                    "notificationsTemplatesEditor.emailEditor.header.previewPlaceholder",
                    `Enter email preview...`,
                  )}
                  className="text-sm"
                  renderInput={({ value, onChange, placeholder }) => (
                    <BlockEditorInput
                      variables={variables}
                      value={value}
                      onChange={onChange}
                      placeholder={placeholder}
                      outputFormat="text"
                      className="pr-16"
                    />
                  )}
                />
              </div>
            )}
          />
        </div>
      </div>
    </FloatingPanelContent>
  );
}

/**
 * "Linked workflows" / "not linked" banner shown above the template name in the
 * tracking editor. Rendered only when the domain config enables it (the return
 * editor hides it), so the workflows query never runs for returns.
 */
function WorkflowDependencyAlert() {
  const { t } = useTranslation("trackoffice");
  const { data: shop } = useCurrentShopSuspense();
  const { currentTemplate } = useTemplatesEditorTemplates();
  const dependantWorkflows = useTemplateWorkflowsDependencies(
    currentTemplate?.id,
  );

  const handleWorkflowClick = (workflowId: string) => () => {
    window.open(
      `/shops/${shop.id}/notifications-workflows/${workflowId}`,
      "_blank",
    );
  };

  if (dependantWorkflows.length > 0) {
    return (
      <Alert className="border-orange-200 bg-orange-50 text-orange-800 [&>svg]:text-orange-600">
        <Info className="h-4 w-4" />
        <AlertTitle className="text-xs mb-2">
          {t(
            "notificationsTemplatesEditor.emailEditor.header.templateUsageDescription",
            `This template is used for ${dependantWorkflows.length} workflow${dependantWorkflows.length > 1 ? "s" : ""}`,
            { count: dependantWorkflows.length || 0 },
          )}
        </AlertTitle>
        <AlertDescription>
          <div className="list-disc list-inside space-y-1">
            {dependantWorkflows.map((workflow) => {
              return (
                <div
                  key={workflow.id}
                  className="flex items-center justify-between gap-1 bg-orange-100 px-2 py-1 rounded-md cursor-pointer hover:bg-orange-200"
                  onClick={handleWorkflowClick(workflow.id)}
                >
                  <span className="text-xs font-medium">{workflow.name}</span>
                  <SquareArrowOutUpRight size={14} />
                </div>
              );
            })}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="border-green-200 bg-green-50 text-green-800 [&>svg]:text-green-600">
      <Info className="h-4 w-4" />
      <AlertTitle>
        {t(
          "notificationsTemplatesEditor.emailEditor.header.templateNotLinkedTitle",
          `Template Not Linked`,
        )}
      </AlertTitle>
      <AlertDescription>
        {t(
          "notificationsTemplatesEditor.emailEditor.header.templateNotLinkedDescription",
          `This template is not currently linked to any notification types.`,
        )}
      </AlertDescription>
    </Alert>
  );
}
